import { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle,
  XCircle,
  Loader2,
  BarChart3,
  Clock,
  Award,
} from "lucide-react";
import { fetchWithAuth } from "@/lib/api/client";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  Legend,
  Cell,
} from "recharts";

// Tipos para el resultado del evaluador
interface Factor {
  name: string;
  score: number;
  weight: number;
  strengths: string[];
  weaknesses: string[];
  evidence: string;
}

interface HistoricalComparison {
  licitacion_title: string;
  similarity: number;
  outcome: "GANADA" | "PERDIDA";
  lessons: string;
}

interface EvaluatorResult {
  probability: number;
  probability_label: "MUY_ALTA" | "ALTA" | "MODERADA-ALTA" | "MODERADA" | "BAJA" | "MUY_BAJA";
  weighted_score: number;
  factors: Factor[];
  historical_comparison?: HistoricalComparison[];
  strategic_recommendations: string[];
  go_no_go: "GO" | "NO-GO" | "REVISAR";
  rationale: string;
}

interface EvaluatorViewProps {
  licitacionId: string;
  initialResult?: EvaluatorResult | null;
}

const defaultProbStyle = { color: "text-gray-600", bg: "bg-gray-50", border: "border-gray-200" };

const probabilityLabelConfig: Record<string, { color: string; bg: string; border: string }> = {
  MUY_ALTA: { color: "text-green-700", bg: "bg-green-100", border: "border-green-300" },
  ALTA: { color: "text-green-600", bg: "bg-green-50", border: "border-green-200" },
  "MODERADA-ALTA": { color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
  MODERADA: { color: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200" },
  MEDIA: { color: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200" },
  BAJA: { color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
  MUY_BAJA: { color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
};

const goNoGoConfig = {
  GO: {
    icon: CheckCircle,
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
    label: "GO - Proceder",
  },
  "NO-GO": {
    icon: XCircle,
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    label: "NO-GO - No proceder",
  },
  REVISAR: {
    icon: BarChart3,
    color: "text-yellow-600",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    label: "REVISAR - Análisis adicional",
  },
};

// Mejora C (changelog cliente): ejes configurables del Evaluador.
// Esta lista es una copia espejo de `AVAILABLE_AXES` del backend
// (backend/app/services/agents/evaluator_agent.py). Si añades un eje
// nuevo o renombras uno, sincroniza ambos sitios. Los slugs son los que
// se mandan al backend en el campo `enabled_axes`.
const EVALUATOR_AXES: { slug: string; label: string; baseWeight: number }[] = [
  { slug: "encaje_tecnico", label: "Encaje técnico", baseWeight: 25 },
  { slug: "experiencia_previa", label: "Experiencia previa", baseWeight: 20 },
  { slug: "competitividad_precio", label: "Competitividad de precio", baseWeight: 20 },
  { slug: "criterios_adjudicacion", label: "Criterios de adjudicación", baseWeight: 15 },
  { slug: "riesgo", label: "Riesgo", baseWeight: 10 },
  { slug: "recursos", label: "Recursos", baseWeight: 10 },
];

export default function EvaluatorView({ licitacionId, initialResult }: EvaluatorViewProps) {
  const [companyProfile, setCompanyProfile] = useState("");
  const [pricingStrategy, setPricingStrategy] = useState<
    "aggressive" | "competitive" | "premium"
  >("competitive");
  const [teamAvailability, setTeamAvailability] = useState("");
  const [includeHistorical, setIncludeHistorical] = useState(true);
  const [enabledAxes, setEnabledAxes] = useState<Set<string>>(
    () => new Set(EVALUATOR_AXES.map((a) => a.slug))
  );
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<EvaluatorResult | null>(null);

  const toggleAxis = (slug: string) => {
    setEnabledAxes((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  useEffect(() => {
    if (initialResult) {
      setResult(initialResult);
    }
  }, [initialResult]);

  const pricingStrategyLabels = {
    aggressive: "Agresivo (precio bajo)",
    competitive: "Competitivo (precio medio)",
    premium: "Premium (precio alto, valor agregado)",
  };

  const handleEvaluate = async () => {
    // Mejora "Perfil empresa": el backend inyecta el perfil global de
    // Configuración → Perfil de empresa. El campo es opcional aquí.

    if (!teamAvailability.trim()) {
      alert("Por favor, describe la disponibilidad del equipo");
      return;
    }

    if (enabledAxes.size < 2) {
      alert("Debes activar al menos 2 ejes de evaluación");
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchWithAuth(`/api/v1/agents/evaluator/execute`, {
        method: "POST",
        body: JSON.stringify({
          licitacion_id: licitacionId,
          input_data: {
            company_profile: companyProfile,
            pricing_strategy: pricingStrategy,
            team_availability: teamAvailability,
            include_historical: includeHistorical,
            // Mejora C (changelog cliente): mandamos solo los ejes activos.
            // Si están todos, mandamos lista vacía/null para que el backend
            // use el catálogo completo (más compacto en el log).
            enabled_axes:
              enabledAxes.size === EVALUATOR_AXES.length
                ? null
                : EVALUATOR_AXES.filter((a) => enabledAxes.has(a.slug)).map((a) => a.slug),
          },
        }),
      });
      // Normalizar datos del agente al formato esperado por el frontend
      const raw = data.result;
      const normalized = {
        ...raw,
        // weighted_score: normalizar overall_score (0-100) a escala 0-10
        weighted_score: raw.weighted_score ?? (raw.overall_score != null ? raw.overall_score / 10 : 5),
        // Normalizar factores
        factors: (raw.factors || []).map((f: Record<string, unknown>) => ({
          ...f,
          // Si score viene en escala de max_score (ej: 20/25), normalizar a 0-10
          score: f.score != null && f.max_score != null
            ? ((f.score as number) / (f.max_score as number)) * 10
            : (f.score as number) ?? 5,
          // Calcular weight desde max_score si no existe (ej: 25/100 = 0.25)
          weight: f.weight ?? (f.max_score != null ? (f.max_score as number) / 100 : 0.2),
          strengths: f.strengths || [],
          weaknesses: f.weaknesses || [],
        })),
      };
      setResult(normalized);
    } catch (error) {
      console.error("Error:", error);
      alert("Error al evaluar probabilidad. Por favor, intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  // Preparar datos para el RadarChart
  const radarData = result
    ? result.factors.map((f) => ({
        factor: f.name.length > 15 ? f.name.substring(0, 15) + "..." : f.name,
        fullName: f.name,
        score: f.score,
        weight: f.weight,
      }))
    : [];

  // Preparar datos para el RadialBarChart de probabilidad
  const radialData = result
    ? [
        {
          name: "Probabilidad",
          value: result.probability * 100,
          fill: result.probability >= 0.7 ? "#16a34a" : result.probability >= 0.5 ? "#2563eb" : "#f59e0b",
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      {/* Descripción del agente */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex gap-3">
        <Award className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-purple-800">
          <p className="font-semibold mb-1">Evaluador de Probabilidad</p>
          <p>
            Analiza los criterios de adjudicación del pliego y evalúa tus posibilidades
            de éxito. Considera tu perfil de empresa, estrategia de precio y disponibilidad
            del equipo para calcular una probabilidad ponderada. Incluye un análisis
            multicriterio con fortalezas, debilidades y recomendaciones estratégicas.
          </p>
        </div>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Configuración del Análisis de Probabilidad
        </h3>

        {/* Perfil de la empresa */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notas adicionales sobre la empresa{" "}
            <span className="text-gray-400 font-normal">opcional</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            El{" "}
            <a
              href="/configuracion/perfil-empresa"
              className="text-purple-600 hover:underline"
            >
              perfil global de empresa
            </a>{" "}
            ya se inyecta automáticamente. Usa este campo para añadir matices
            relevantes solo a este análisis.
          </p>
          <textarea
            value={companyProfile}
            onChange={(e) => setCompanyProfile(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows={4}
            placeholder="Ej.: para esta licitación, foco en la experiencia con menores no acompañados…"
          />
        </div>

        {/* Estrategia de precio */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estrategia de Precio
          </label>
          <div className="flex gap-2">
            {(["aggressive", "competitive", "premium"] as const).map((strategy) => (
              <button
                key={strategy}
                onClick={() => setPricingStrategy(strategy)}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  pricingStrategy === strategy
                    ? "bg-purple-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {pricingStrategyLabels[strategy]}
              </button>
            ))}
          </div>
        </div>

        {/* Disponibilidad de equipo */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Disponibilidad del Equipo
          </label>
          <textarea
            value={teamAvailability}
            onChange={(e) => setTeamAvailability(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows={3}
            placeholder="Describe la disponibilidad de tu equipo técnico, plazos comprometidos, carga de trabajo actual, etc."
          />
        </div>

        {/* Incluir histórico */}
        <div className="mb-4 flex items-center">
          <button
            onClick={() => setIncludeHistorical(!includeHistorical)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              includeHistorical ? "bg-purple-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                includeHistorical ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <label className="ml-3 text-sm font-medium text-gray-700">
            Incluir comparación con histórico de licitaciones similares
          </label>
        </div>

        {/* Mejora C (changelog cliente): ejes configurables del radar */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ejes a evaluar{" "}
            <span className="text-gray-400 font-normal">
              ({enabledAxes.size} de {EVALUATOR_AXES.length})
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            {EVALUATOR_AXES.map((axis) => {
              const active = enabledAxes.has(axis.slug);
              return (
                <button
                  key={axis.slug}
                  type="button"
                  onClick={() => toggleAxis(axis.slug)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? "bg-purple-100 text-purple-800 border-purple-300"
                      : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                  }`}
                  title={
                    active
                      ? `Desactivar "${axis.label}" del radar`
                      : `Activar "${axis.label}" en el radar`
                  }
                  aria-pressed={active}
                >
                  {axis.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Desactiva los ejes que no apliquen a esta licitación. Los pesos
            de los ejes activos se renormalizan automáticamente para sumar
            100%. Mínimo 2 ejes activos.
          </p>
        </div>

        {/* Botón de acción */}
        <button
          onClick={handleEvaluate}
          disabled={isLoading || !teamAvailability.trim() || enabledAxes.size < 2}
          className="w-full bg-purple-500 text-white font-medium py-2 px-4 rounded-md hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Evaluando...
            </>
          ) : (
            <>
              <Award className="w-5 h-5 mr-2" />
              Evaluar Probabilidad
            </>
          )}
        </button>
      </div>

      {/* Resultados */}
      {result && (
        <div className="space-y-6">
          {/* Probabilidad Principal */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">
              Probabilidad de Éxito
            </h3>
            <div className="flex flex-col md:flex-row items-center justify-around gap-8">
              {/* Gráfico Radial */}
              <div className="relative w-64 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="90%"
                    barSize={20}
                    data={radialData}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                    <RadialBar
                      background
                      dataKey="value"
                      cornerRadius={10}
                      fill={radialData[0]?.fill}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                {/* Texto central */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-5xl font-bold text-gray-800">
                    {Math.round(result.probability * 100)}%
                  </div>
                  <div className="text-sm text-gray-500 mt-1">Probabilidad</div>
                </div>
              </div>

              {/* Label y score ponderado */}
              <div className="text-center space-y-4">
                <div
                  className={`inline-block px-6 py-3 rounded-lg border-2 ${
                    (probabilityLabelConfig[result.probability_label] || defaultProbStyle).bg
                  } ${(probabilityLabelConfig[result.probability_label] || defaultProbStyle).border} ${
                    (probabilityLabelConfig[result.probability_label] || defaultProbStyle).color
                  }`}
                >
                  <div className="text-2xl font-bold">{result.probability_label}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-500">Score Ponderado</div>
                  <div className="text-3xl font-bold text-gray-800">
                    {(result.weighted_score ?? 0).toFixed(1)}/10
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Radar Chart - Análisis Multicriterio */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Análisis Multicriterio
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="factor" />
                  <PolarRadiusAxis angle={90} domain={[0, 10]} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#9333ea"
                    fill="#9333ea"
                    fillOpacity={0.6}
                  />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Factores Detallados */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Análisis por Factor</h3>
            {(result.factors || []).map((factor, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                {/* Header del factor */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h4 className="text-md font-semibold text-gray-800">{factor.name}</h4>
                    <span className="text-sm text-gray-500">
                      (Peso: {Math.round(factor.weight * 100)}%)
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    {(factor.score ?? 0).toFixed(1)}/10
                  </div>
                </div>

                {/* Barra de progreso */}
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
                  <div
                    className="bg-purple-500 h-full transition-all duration-500"
                    style={{ width: `${(factor.score / 10) * 100}%` }}
                  />
                </div>

                {/* Fortalezas y Debilidades */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Fortalezas */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <h5 className="text-sm font-semibold text-green-700">Fortalezas</h5>
                    </div>
                    <ul className="space-y-1">
                      {(factor.strengths || []).map((strength, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start">
                          <span className="text-green-600 mr-2">•</span>
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Debilidades */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      <h5 className="text-sm font-semibold text-red-700">Debilidades</h5>
                    </div>
                    <ul className="space-y-1">
                      {(factor.weaknesses || []).map((weakness, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start">
                          <span className="text-red-600 mr-2">•</span>
                          <span>{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Evidencia */}
                {factor.evidence && (
                  <div className="mt-4 bg-gray-50 rounded p-3 border-l-4 border-gray-300">
                    <h5 className="text-sm font-semibold text-gray-700 mb-1">Evidencia</h5>
                    <p className="text-sm text-gray-600 italic">{factor.evidence}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Comparación Histórica */}
          {result.historical_comparison && result.historical_comparison.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Comparación con Licitaciones Similares
                </h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.historical_comparison.map((hist, index) => (
                  <div
                    key={index}
                    className={`rounded-lg border-2 p-4 ${
                      hist.outcome === "GANADA"
                        ? "border-green-200 bg-green-50"
                        : "border-red-200 bg-red-50"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h5 className="text-sm font-semibold text-gray-800 line-clamp-2">
                        {hist.licitacion_title}
                      </h5>
                      {hist.outcome === "GANADA" ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                    <div className="mb-2">
                      <span className="text-xs text-gray-600">Similitud:</span>
                      <span className="ml-2 text-sm font-semibold text-gray-800">
                        {Math.round(hist.similarity * 100)}%
                      </span>
                    </div>
                    <div className="text-xs text-gray-700 bg-white rounded p-2 border border-gray-200">
                      {hist.lessons}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recomendaciones Estratégicas */}
          {result.strategic_recommendations && result.strategic_recommendations.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">
                Recomendaciones Estratégicas
              </h3>
              <ol className="space-y-3">
                {result.strategic_recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold mr-3 mt-0.5 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-sm text-blue-800 flex-1">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Decisión Go/No-Go */}
          {result.go_no_go && goNoGoConfig[result.go_no_go] && (
          <div
            className={`rounded-lg border-2 ${goNoGoConfig[result.go_no_go].border} ${
              goNoGoConfig[result.go_no_go].bg
            } p-6`}
          >
            <div className="flex items-start">
              {(() => {
                const GoNoGoIcon = goNoGoConfig[result.go_no_go].icon;
                return (
                  <GoNoGoIcon
                    className={`w-7 h-7 mr-3 mt-0.5 flex-shrink-0 ${
                      goNoGoConfig[result.go_no_go].color
                    }`}
                  />
                );
              })()}
              <div>
                <h3
                  className={`text-xl font-bold mb-2 ${goNoGoConfig[result.go_no_go].color}`}
                >
                  {goNoGoConfig[result.go_no_go].label}
                </h3>
                <p className="text-sm text-gray-700">{result.rationale}</p>
              </div>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
